// NB: This file and its re-exports are in @kapitalist/contract, the shared API contract between
// @kapitalist/frontend and @kapitalist/backend.
// Changes here will require work on both sides and should only be made with care.

import type {EmptyObject} from '@kapitalist/utils/EmptyObject'
import type {
  TCreateMajorAction,
  TCreateMinorAction,
  TGetGameDataResponse,
  TGetGameTimeResponse,
  TSubmitMajorActionResponse,
  TSubmitMinorActionResponse,
  TAdminSignUpsResponse,
  TChangeEmailParams,
  TChangePasswordParams,
  TChangeUsernameParams,
  TSignInParams,
  TSignUpParams,
  TGetInitialDataResponse,
  TDashboardDataResponse,
  THealthCheckResponse,
  TCreateGameResponse,
  TWaitingRoomResponse,
} from './bodies'
import {defineEndpoint} from './defineEndpoint'

export * from './bodies'

/**
 * Leaves are the single source for a given backend endpoint's HTTP verb, route pattern,
 * path builder, and request and response bodies.
 *
 * Reads are GET, mutations are POST, QUERY for reads that need a request body.
 */
export const endpoints = {
  etc: {
    home: defineEndpoint<EmptyObject, THealthCheckResponse>()('GET', '/'),
    robots: defineEndpoint<EmptyObject, string>()('GET', '/robots.txt'),
  },

  auth: {
    signin: defineEndpoint<TSignInParams, TGetInitialDataResponse>()(
      'POST',
      '/signin'
    ),
    signup: defineEndpoint<TSignUpParams, EmptyObject>()('POST', '/signup'),
    logout: defineEndpoint<EmptyObject, EmptyObject>()('POST', '/logout'),
  },

  account: {
    changeEmail: defineEndpoint<TChangeEmailParams, TGetInitialDataResponse>()(
      'POST',
      '/account/email'
    ),
    changeUsername: defineEndpoint<TChangeUsernameParams, TGetInitialDataResponse>()(
      'POST',
      '/account/username'
    ),
    changePassword: defineEndpoint<TChangePasswordParams, EmptyObject>()(
      'POST',
      '/account/password'
    ),
  },

  core: {
    getInitialData: defineEndpoint<EmptyObject, TGetInitialDataResponse>()(
      'GET',
      '/get-player-data'
    ),
  },

  dashboard: {
    dashboardData: defineEndpoint<EmptyObject, TDashboardDataResponse>()(
      'GET',
      '/dashboard-data'
    ),
    markGameAsStale: defineEndpoint<EmptyObject, TDashboardDataResponse>()(
      'POST',
      '/game/:gameId/mark-stale'
    ),
  },

  pregame: {
    createGame: defineEndpoint<EmptyObject, TCreateGameResponse>()(
      'POST',
      '/create-game'
    ),
    joinGame: defineEndpoint<EmptyObject, EmptyObject>()(
      'POST',
      '/join-game/:maybeGameId'
    ),
    startGame: defineEndpoint<EmptyObject, EmptyObject>()(
      'POST',
      '/start-game/:gameId'
    ),
    gameWaitingRoom: defineEndpoint<EmptyObject, TWaitingRoomResponse>()(
      'GET',
      '/waiting-room/:maybeGameId'
    ),
    kickFromWaitingRoom: defineEndpoint<EmptyObject, TWaitingRoomResponse>()(
      'POST',
      '/waiting-room/:gameId/kick-player/:playerId'
    ),
  },

  gameplay: {
    getGameData: defineEndpoint<EmptyObject, TGetGameDataResponse>()(
      'GET',
      '/game/:maybeGameId/get-game-data'
    ),
    getGameTime: defineEndpoint<EmptyObject, TGetGameTimeResponse>()(
      'GET',
      '/game/:maybeGameId/time'
    ),
    submitMajorAction: defineEndpoint<
      TCreateMajorAction,
      TSubmitMajorActionResponse
    >()('POST', '/game/:gameId/submit-major-action'),
    submitMinorAction: defineEndpoint<
      TCreateMinorAction,
      TSubmitMinorActionResponse
    >()('POST', '/game/:gameId/submit-minor-action'),
  },

  spectator: {
    requestSpectatorPermission: defineEndpoint<EmptyObject, TGetGameDataResponse>()(
      'POST',
      '/game/:gameId/spectate-request/:spectatedPlayerId'
    ),
    approveSpectatorRequest: defineEndpoint<EmptyObject, TGetGameDataResponse>()(
      'POST',
      '/game/:gameId/spectate-approve/:spectatorPlayerId'
    ),
    revokeSpectatorPermission: defineEndpoint<EmptyObject, TGetGameDataResponse>()(
      'POST',
      '/game/:gameId/spectate-revoke/:spectatorPlayerId'
    ),
  },

  admin: {
    listSignUps: defineEndpoint<EmptyObject, TAdminSignUpsResponse>()(
      'GET',
      '/admin/signups'
    ),
    approveSignUp: defineEndpoint<EmptyObject, TAdminSignUpsResponse>()(
      'POST',
      '/admin/signup/:signupRequestId/approve'
    ),
  },
} as const
